import React from 'react';
import { Button, Divider, Typography } from 'antd';
import { OrganizationBusinessType } from '@institutions/types';
import {
  FilteredTable,
  Icon,
  Link,
  TextSearchTableFilter,
} from '@common/components';
import { usePaginationHelper, useQuery } from '@common/hooks';
import { PAGINATED_WORKFORCE_ORGANIZATION_BUSINESS_CONFIG } from '@institutions/graphql';
import Text from 'antd/lib/typography/Text';
import {
  InputMaybe,
  OrderDirection,
  WfgOrgBusinessConfigFragment,
  WorkforceGroupOrgBusinessConfigOrderField,
} from '@common/graphql/schema';
import { convertToType } from '@scheduling/services';
import { buildDialogUrl } from '@common/services';

const { Title } = Typography;

type OrganizationFacilityGroupsSectionProps = {
  facility?: OrganizationBusinessType;
};

export function OrganizationFacilityGroupsSection({
  facility,
}: OrganizationFacilityGroupsSectionProps) {
  const LOCALSTORAGE_FILTER_KEY = 'FacilityGroupsFilters';
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: LOCALSTORAGE_FILTER_KEY,
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'NAME',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'NAME',
      earliestAdvanceTime: 'EARLIEST_ADVANCE_TIME',
      openShiftsVisibilityDelayHours: 'OPEN_SHIFTS_VISIBILITY_DELAY_HOURS',
    },
  });

  const { loading, data } = useQuery(
    PAGINATED_WORKFORCE_ORGANIZATION_BUSINESS_CONFIG,
    {
      variables: {
        limit: paginationVariables.limit,
        offset: paginationVariables.offset,
        orderDirection: paginationVariables.orderDirection as
          | InputMaybe<OrderDirection>
          | undefined,
        orderField: paginationVariables.orderField as
          | InputMaybe<WorkforceGroupOrgBusinessConfigOrderField>
          | undefined,
        filters: filters,
        businessId: facility?.id || '',
      },
      skip: !facility?.id,
    }
  );

  const displayDays = (hours: number) => {
    const days = convertToType(String(hours), 'hours', 'days');

    return Number.isInteger(days) ? days : days.toFixed(1);
  };

  const displayConfig = (config: WfgOrgBusinessConfigFragment) => {
    if (config.useDefaultSettings && config.workforceGroup?.defaultConfig) {
      return config.workforceGroup.defaultConfig;
    }

    return config;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'workforceGroup',
      key: 'name',
      width: '30%',
      render: (
        workforceGroup: WfgOrgBusinessConfigFragment['workforceGroup']
      ) => {
        return <Text>{workforceGroup?.name}</Text>;
      },
      sorter: true,
      filtered: !!filters.name || false,
      filteredValue: filters.name || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      title: 'Default Settings',
      dataIndex: 'useDefaultSettings',
      key: 'name',
      width: '10%',
      render: (useDefaultSettings: boolean) => {
        if (!useDefaultSettings) {
          return '';
        }
        return (
          <div
            style={{ display: 'flex', alignItems: 'center' }}
            className="default-setting-on"
          >
            <Icon icon="check" size="lg" color="steelDarkest" />
          </div>
        );
      },
    },
    {
      title: 'Shift Visibility',
      key: 'earliestAdvanceTime',
      dataIndex: 'earliestAdvanceTime',
      width: '20%',
      render: (
        _earliestAdvanceTime: number,
        config: WfgOrgBusinessConfigFragment
      ) => {
        const { earliestAdvanceTime, gateEarliestAdvanceTime } =
          displayConfig(config);
        if (
          !earliestAdvanceTime ||
          earliestAdvanceTime === 0 ||
          !gateEarliestAdvanceTime
        ) {
          return '';
        }

        return (
          <Text>
            {displayDays(earliestAdvanceTime)}{' '}
            {earliestAdvanceTime === 1 ? 'day' : 'days'}
          </Text>
        );
      },
      sorter: true,
    },
    {
      title: 'Schedule Delay',
      dataIndex: 'openShiftsVisibilityDelayHours',
      width: '20%',
      key: 'openShiftsVisibilityDelayHours',
      render: (
        _openShiftsVisibilityDelayHours: number,
        config: WfgOrgBusinessConfigFragment
      ) => {
        const { openShiftsVisibilityDelayHours, gateOpenShiftsVisibility } =
          displayConfig(config);
        if (
          !openShiftsVisibilityDelayHours ||
          openShiftsVisibilityDelayHours === 0 ||
          !gateOpenShiftsVisibility
        ) {
          return '';
        }

        return (
          <Text>
            {displayDays(openShiftsVisibilityDelayHours)}{' '}
            {openShiftsVisibilityDelayHours === 1 ? 'day' : 'days'}
          </Text>
        );
      },
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '6%',
      render: (_: unknown, config: WfgOrgBusinessConfigFragment) => (
        <Link
          to={buildDialogUrl('workforce-group-org-business-update-drawer', {
            props: { wfgConfigId: config.id },
          })}
        >
          <Button type="link" style={{ paddingLeft: 0 }}>
            Edit
          </Button>
        </Link>
      ),
    },
  ];

  const dataSource =
    data?.paginatedWorkforceGroupOrgBusinessConfig
      .workforceGroupOrganizationBusinessConfigs || [];

  return (
    <section className="organization-facility__details">
      <div className="organization-facility__details-title">
        <Title level={3} style={{ marginRight: '12px' }}>
          Groups
        </Title>
      </div>
      <Divider />

      <FilteredTable
        rowKey="id"
        className="FacilityGroupsTable"
        columns={columns}
        loading={loading}
        dataSource={dataSource}
        onFilterChange={filterChangeHandler}
        pagination={{
          ...paginationVariables,
          total:
            data?.paginatedWorkforceGroupOrgBusinessConfig?.totalCount || null,
        }}
        localStorageFilterKey={localStorageFilterKey}
        onChange={tableChangeHandler}
        filters={filters}
        scroll={{ x: 'max-content' }}
      />
      <style jsx>{`
        .organization-facility__details-title {
          margin-top: 24px;
          display: flex;
        }
      `}</style>
    </section>
  );
}
